import React, { useEffect, useRef, useState } from "react"
import * as Yup from "yup"
import { useQueryClient } from "react-query"
import { Form, Formik, FormikHelpers } from "formik"
import { toast } from "react-toastify"
import axios, { AxiosError } from "axios"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"

// Constants & Models
import {
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MONTANT_INDEMNITE_PAR_KM,
     CONST_NDF_DETAILS_CATEGORIE_KM,
     CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
} from "@common-constants/*"
import { IConsultantNdfDetailDocumentModel, IConsultantNdfDetailModel, IHttpErrorResponseModel } from "@common-models/*"
import { INdfDetailEditDepenseSimpleRequestModel } from "../../../../../core/_models"
import { ConsultantNdfDetailModel } from "../../../../../../../../models"

// Utils
import MyAlert from "../../../../../../../../utils/MyAlert"
import MySimpleSpinner from "../../../../../../../../utils/MySimpleSpinner"
import FileAndDriveHelper from "../../../../../../../../helpers/FileAndDriveHelper"
import MyInputFormField from "@common-components/fields/MyInputFormField"

// Requests
import { ndfDetailAddDocument, ndfDetailDocumentDelete, ndfDetailDocumentReplace, ndfDetailEditDepenseSimple } from "../../../../../core/_request"

// Context & Subcomponents
import { useNdfDetailsSalarieContext } from "../../../NdfDetails/salarie/NdfDetailsSalarie"
import { API_URL_ESPACE_AVENTURIER } from "../../../../../../../../constants"
import { NdfBasicFieldsSection } from "./components/NdfBasicFieldsSection"
import { NdfMealSection } from "./components/NdfMealSection"
import { NdfMileageSection } from "./components/NdfMileageSection"
import { NdfCommentSection } from "./components/NdfCommentSection"
import { NdfJustificatifsEditSection } from "./components/NdfJustificatifsEditSection"

interface INdfUpdateDetailsEditDepenseSimplePropsModel {
     detail: IConsultantNdfDetailModel
}

const NdfEditDepenseSimple = ({ detail }: INdfUpdateDetailsEditDepenseSimplePropsModel) => {
     const queryClient = useQueryClient()
     const context = useNdfDetailsSalarieContext()
     const MySwal = withReactContent(Swal)

     // --- ÉTATS POUR LE LIVE EDIT DES DOCUMENTS ---
     const [localDocuments, setLocalDocuments] = useState<IConsultantNdfDetailDocumentModel[]>(detail.consultantNdfDocuments || [])
     const [isLoadingDocAction, setIsLoadingDocAction] = useState(false)
     const [docToBeReplaced, setDocToBeReplaced] = useState<IConsultantNdfDetailDocumentModel>()
     const refReplaceDocInput = useRef<HTMLInputElement>(null)
     const refAddDocInput = useRef<HTMLInputElement>(null)

     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState({
          distanceEnKmEntreDepartEtDestination: 0,
          distanceTotal: 0,
          montantTTC: 0,
     })

     const validationSchema = Yup.object().shape({
          categorie: Yup.string().label("Catégorie").required("Champ requis"),
          date: Yup.string()
               .label("Date")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.notRequired(),
                    otherwise: schema => schema.required("Champ requis"),
               }),
          montantTTC: Yup.number()
               .label("Montant TTC")
               .when("categorie", {
                    is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),
          moisAnneeKm: Yup.string()
               .label("Mois du trajet")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Sélectionnez le mois"),
               }),
          joursKm: Yup.array()
               .label("Jours sélectionnés")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema =>
                         schema.min(1, "Sélectionnez au moins un jour").test("exact", "Sélectionnez autant de jours que de trajets", function (val) {
                              return val?.length === this.parent.nbTrajets
                         }),
               }),
          description: Yup.string()
               .label("Description")
               .when("categorie", {
                    is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Champ requis"),
               }),
     })

     const initialValues: INdfDetailEditDepenseSimpleRequestModel = {
          date: detail.date?.format("YYYY-MM-DD") || "",
          categorie: detail.categorie as string,
          montantTTC: detail.montantTtc ? Number(detail.montantTtc.toFixed(2)) : 0,
          description: detail.description || "",
          repasSeulOuEnGroupe: detail.categorie === CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI ? (detail.invites ? "REPAS_MIDI_EN_GROUPE" : "REPAS_MIDI_SEUL") : undefined,
          invites: detail.invites || undefined,
          adresseDepart: detail.indemniteKmDetails?.addresseDepart || "",
          adresseArrive: detail.indemniteKmDetails?.addresseArrive || "",
          nbTrajets: detail.indemniteKmDetails?.nbTrajets,
          isAllerRetour: detail.indemniteKmDetails?.isAllerRetour || false,
          distanceTotale: detail.indemniteKmDetails?.distanceKmTotal || 0,
          moisAnneeKm: detail.date?.format("YYYY-MM") || "",
          joursKm: detail.indemniteKmDetails?.joursKm || [],
     }

     useEffect(() => {
          if (detail.indemniteKmDetails) {
               setDetailsIndemniteKm({
                    distanceEnKmEntreDepartEtDestination: detail.indemniteKmDetails.distanceKmEntreDepartEtArrive || 0,
                    distanceTotal: detail.indemniteKmDetails.distanceKmTotal || 0,
                    montantTTC: Number(((detail.indemniteKmDetails.distanceKmTotal || 0) * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)),
               })
          }
     }, [detail])

     const handleEditSubmit = (values: INdfDetailEditDepenseSimpleRequestModel, helpers: FormikHelpers<INdfDetailEditDepenseSimpleRequestModel>) => {
          helpers.setSubmitting(true)
          helpers.setStatus(null)
          ndfDetailEditDepenseSimple(detail.id as number, values)
               .then(r => {
                    queryClient.setQueryData<ConsultantNdfDetailModel[] | undefined>(["ndfDetails", context.ndfId], (prev: ConsultantNdfDetailModel[] | undefined) => {
                         if (prev) {
                              const index = prev.findIndex(item => item.id === detail.id)
                              if (index >= 0) {
                                   prev[index] = {
                                        ...prev[index],
                                        ...r.data,
                                   } as any
                              }
                         }
                         context.computeTotalExpenses((prev || []) as any)
                         return prev
                    })

                    helpers.setSubmitting(false)
                    toast.success("Vos modifications ont bien été prises en compte.")
                    context.setCurrentDepenseInEdition(undefined)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key as any, error.errors[key])
                    }

                    if (error?.code === "REGULARATION_IS_REQUIRED") {
                         helpers.setStatus(
                              <>
                                   <p>{error?.detail}</p>
                                   <b className={"mb-2 fs-5"}>Merci de suivre les étapes suivantes:</b>
                                   <ul>
                                        <li>Accédez à la liste des déclarations.</li>
                                        <li>Demandez la régularisation de la déclaration du mois concerné.</li>
                                        <li>Attendez la validation de votre responsable (utilisez ce temps pour finaliser la déclaration du mois en cours).</li>
                                        <li>Une fois la demande validée, ajoutez votre dépense et vérifiez qu'il n'y a plus de ticket restaurant.</li>
                                        <li>Finaliser et signer votre déclaration.</li>
                                   </ul>
                              </>
                         )
                    } else {
                         helpers.setStatus(error?.detail)
                    }
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     // --- HANDLERS DOCUMENTS (Mise à jour locale et Loaders) ---
     const onAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file) {
               setIsLoadingDocAction(true)
               FileAndDriveHelper.processFiles([file]).then(processedFiles => {
                    ndfDetailAddDocument(detail.id as number, processedFiles[0])
                         .then(r => {
                              setLocalDocuments(prev => [...prev, r.data as any])
                              queryClient.invalidateQueries(["ndfDetails", context.ndfId])
                              toast.success("Le justificatif a bien été ajouté")
                         })
                         .finally(() => {
                              setIsLoadingDocAction(false)
                              if (refAddDocInput.current) refAddDocInput.current.value = ""
                         })
               })
          }
     }

     const onReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file && docToBeReplaced) {
               setIsLoadingDocAction(true)
               ndfDetailDocumentReplace(docToBeReplaced.id as number, file)
                    .then(r => {
                         setLocalDocuments(prev =>
                              prev.map(doc => {
                                   if (doc.id === docToBeReplaced.id) {
                                        return {
                                             ...doc,
                                             googleDriveID: r.data.googleDriveID,
                                             googleDriveMimeType: r.data.googleDriveMimeType,
                                        } as IConsultantNdfDetailDocumentModel
                                   }
                                   return doc
                              })
                         )
                         queryClient.invalidateQueries(["ndfDetails", context.ndfId])
                         toast.success("Le justificatif a bien été remplacé")
                    })
                    .finally(() => {
                         setIsLoadingDocAction(false)
                         setDocToBeReplaced(undefined)
                         if (refReplaceDocInput.current) refReplaceDocInput.current.value = ""
                    })
          }
     }

     const handleRemoveDoc = (id: number, idx: number) => {
          MySwal.fire({
               title: `Supprimer le justificatif n°${idx}?`,
               icon: "warning",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
               focusConfirm: true,
          }).then(r => {
               if (r.isConfirmed) {
                    setIsLoadingDocAction(true)
                    ndfDetailDocumentDelete(id)
                         .then(() => {
                              setLocalDocuments(prev => prev.filter(doc => doc.id !== id))
                              queryClient.invalidateQueries(["ndfDetails", context.ndfId])
                              toast.success("Le justificatif a bien été supprimé.")
                         })
                         .finally(() => setIsLoadingDocAction(false))
               }
          })
     }

     // FIX : On supprime l'event 'e' car NdfJustificatifsEditSection ne le renvoie pas,
     // et on utilise setIsLoadingDocAction(true) pour bloquer les boutons à la place.
     const handleViewJustificatif = (reference: string, mimeType: string) => {
          setIsLoadingDocAction(true) // Désactive les boutons du composant NdfJustificatifsEditSection
          toast.info("Chargement du justificatif en cours ...", { autoClose: false, toastId: "loading-doc" })

          axios.post(
               `${API_URL_ESPACE_AVENTURIER}/download-document`,
               { reference },
               {
                    responseType: "arraybuffer",
               }
          )
               .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }))
                    window.open(url)
                    toast.dismiss("loading-doc")
               })
               .catch((err: AxiosError | any) => {
                    toast.dismiss("loading-doc")
                    toast.error(err.response?.data?.detail || "Une erreur est survenue lors du téléchargement.", { autoClose: false })
               })
               .finally(() => {
                    setIsLoadingDocAction(false) // Réactive les boutons
               })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleEditSubmit} validationSchema={validationSchema}>
               {helpers => {
                    const isKm = helpers.values.categorie === CONST_NDF_DETAILS_CATEGORIE_KM
                    const showCommentAndSubmit = !isKm || (isKm && !!helpers.values.moisAnneeKm)

                    return (
                         <Form noValidate>
                              {helpers.status && (
                                   <MyAlert type="danger" classNames="mb-4">
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              <div className="row g-9">
                                   <div className={`col-lg-7 col-xl-8 order-2 order-lg-1 ${!helpers.values.categorie ? "mx-auto" : ""}`}>
                                        <div className="d-flex flex-column gap-4">
                                             <NdfBasicFieldsSection helpers={helpers} validationSchema={validationSchema} />

                                             {helpers.values.categorie && (
                                                  <div>
                                                       <div className="my-4 separator separator-dashed" />
                                                       <NdfMealSection helpers={helpers} validationSchema={validationSchema} />

                                                       {!isKm && (
                                                            <MyInputFormField
                                                                 formikProps={helpers}
                                                                 validationSchema={validationSchema}
                                                                 name="montantTTC"
                                                                 label={<div className="required">Montant TTC</div>}
                                                                 type="number"
                                                                 inputMode="decimal"
                                                                 placeholder="Saisissez le montant TTC"
                                                                 classNameLabel="col-lg-4"
                                                                 classNameField="col-lg-8"
                                                            />
                                                       )}

                                                       <NdfMileageSection
                                                            helpers={helpers}
                                                            validationSchema={validationSchema}
                                                            detailsIndemniteKm={detailsIndemniteKm}
                                                            setDetailsIndemniteKm={setDetailsIndemniteKm}
                                                       />

                                                       {showCommentAndSubmit && (
                                                            <div>
                                                                 <NdfCommentSection helpers={helpers} validationSchema={validationSchema} />
                                                                 <div className="separator separator-dashed my-6" />
                                                                 <div className="text-center d-flex justify-content-end">
                                                                      <button className="btn btn-sm btn-primary" type="submit" disabled={helpers.isSubmitting}>
                                                                           {!helpers.isSubmitting ? (
                                                                                <>
                                                                                     Enregistrer <i className="fas fa-check-circle ms-2" />
                                                                                </>
                                                                           ) : (
                                                                                <MySimpleSpinner size="sm" />
                                                                           )}
                                                                      </button>
                                                                 </div>
                                                            </div>
                                                       )}
                                                  </div>
                                             )}
                                        </div>
                                   </div>

                                   {helpers.values.categorie && (
                                        <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0">
                                             <div className="position-sticky" style={{ top: "25px" }}>
                                                  <NdfJustificatifsEditSection
                                                       documents={localDocuments as any}
                                                       isLoading={isLoadingDocAction}
                                                       onAddClick={() => refAddDocInput.current?.click()}
                                                       onReplaceClick={doc => {
                                                            setDocToBeReplaced(doc as IConsultantNdfDetailDocumentModel)
                                                            refReplaceDocInput.current?.click()
                                                       }}
                                                       onRemoveClick={handleRemoveDoc}
                                                       // FIX : passage propre à la fonction sans 'e'
                                                       onViewClick={(doc: any) => handleViewJustificatif(doc.googleDriveID as string, doc.googleDriveMimeType as string)}
                                                       isKm={isKm}
                                                  />
                                             </div>
                                        </div>
                                   )}
                              </div>

                              <input type="file" className="d-none" ref={refAddDocInput} onChange={onAddFile} />
                              <input type="file" className="d-none" ref={refReplaceDocInput} onChange={onReplaceFile} />
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default NdfEditDepenseSimple
